import { PriceChangesChart } from '@design/charts/price-changes-chart/PriceChangesChart';
import { DataPoint } from '@design/charts/price-changes-chart/PriceChangesChart.types';
import { NEW_YORK_TIMEZONE } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { CatalystSingleMarketReactionPrice } from '@terminal/modules/market-reaction-live-price/catalyst-market-reaction-price/CatalystSingleMarketReactionPrice';
import { CatalystMultipleMarketReactionPrice } from '@terminal/modules/market-reaction-live-price/catalyst-multiple-market-reaction-price/CatalystMultipleMarketReactionPrice';
import type { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import clsx from 'clsx';
import { getDate, getMonth, getYear, isAfter } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CatalystMarketReactionChartHeaderVariant,
  LineData,
} from '../types';
import styles from './CatalystMarketReactionChart.module.scss';

export interface CatalystMarketReactionChartProps {
  fromDateEST?: Date;
  toDateEST?: Date;
  lines: LineData[];
  leftHeaderNode?: React.ReactNode;
  isDailyFallback?: boolean;
  chartHeaderVariant?: CatalystMarketReactionChartHeaderVariant;
  chartActionTrackingSource?: PosthogTrackingSource;
}

export const CatalystMarketReactionChart = ({
  fromDateEST,
  toDateEST,
  lines,
  leftHeaderNode,
  isDailyFallback = false,
  chartHeaderVariant = 'default',
  chartActionTrackingSource,
}: CatalystMarketReactionChartProps) => {
  const { t } = useTranslation(['insights', 'chart']);
  const { colors } = useChartTheme(['soft']);
  const fillColors = {
    positive: colors.threshold.positive.value,
    negative: colors.threshold.negative.value,
    neutral: colors.threshold.neutral.value,
  };

  if (!lines.length || !toDateEST) {
    return null;
  }

  const isAfterHoursChart = !isDailyFallback && toDateEST.getHours() > 16;

  const getVerticalLines = () => {
    if (!fromDateEST || isDailyFallback) {
      return [];
    }

    const y = getYear(fromDateEST);
    const m = getMonth(fromDateEST);
    const d = getDate(fromDateEST);

    const verticalLines: { x: Date; isDashed?: boolean }[] = [
      { x: new Date(y, m, d, 9, 30, 0, 0) },
      { x: new Date(y, m, d, 16, 0, 0, 0), isDashed: true },
    ];

    return verticalLines;
  };

  const nowEST = toZonedTime(new Date(), NEW_YORK_TIMEZONE);
  const isWindowComplete = isDailyFallback || isAfter(nowEST, toDateEST);

  const multiColors = colors.getMulti(lines.length);
  const chartLines = lines.map((line, i) => {
    const data: DataPoint[] = line.data.map(item => ({
      date: isDailyFallback
        ? new Date(item.time)
        : toZonedTime(new Date(item.time), NEW_YORK_TIMEZONE),
      value: item.value,
    }));
    return {
      data,
      label: line.label,
      color:
        lines.length > 1 ? multiColors[i].value : colors.line.default.value,
    };
  });

  const isMultipleLines = lines.length > 1;
  const isPercentChart = isMultipleLines;

  const mergedValues = lines
    .flatMap(line => line.data.map(item => item.value))
    .filter((value): value is number => value !== undefined);

  const range =
    mergedValues.length > 0
      ? Math.max(...mergedValues) - Math.min(...mergedValues)
      : 0;
  const yAxisFormatType = isPercentChart
    ? range > 4
      ? 'percent'
      : 'percent1f'
    : 'default';

  const firstLine = lines[0];
  const firstSubject = firstLine.subject;
  return (
    <div className={clsx(styles.container, styles[chartHeaderVariant])}>
      {isMultipleLines ? (
        <CatalystMultipleMarketReactionPrice
          lines={lines}
          lineColors={multiColors.map(c => c.value)}
          isWindowComplete={isWindowComplete}
          isAfterHoursChart={isAfterHoursChart}
          chartHeaderVariant={chartHeaderVariant}
          trackingSource={chartActionTrackingSource}
        />
      ) : (
        firstSubject.type === 'entity' && (
          <div className={styles.chartHeaderWrapper}>
            <div className={styles.leftHeaderNode}>{leftHeaderNode}</div>
            <div className={styles.priceWrapper}>
              <CatalystSingleMarketReactionPrice
                entity={firstSubject.data}
                isWindowComplete={isWindowComplete}
                isAfterHoursChart={isAfterHoursChart}
                eventProportionChange={firstLine.lastProportionChange}
                lastKnownPrice={firstLine.lastKnownPrice}
                lastKnownDate={firstLine.lastKnownDate}
                prevCloseData={firstLine.prevCloseData}
              />
            </div>
          </div>
        )
      )}
      {!!lines.length && (
        <PriceChangesChart
          fromDateEST={fromDateEST}
          toDateEST={toDateEST}
          lines={chartLines}
          hasTooltip
          tooltipFormatType={isPercentChart ? 'percent2f' : 'default2f'}
          fillColors={fillColors}
          useTimeScale
          useCalendarDateAxis={isDailyFallback}
          yAxisFormatType={yAxisFormatType}
          verticalLines={getVerticalLines()}
          prevClosePrice={
            !isPercentChart ? firstLine.prevCloseData?.close : undefined
          }
          threshold={
            !isPercentChart && chartLines.length === 1
              ? firstLine.prevCloseData?.close
              : undefined
          }
          thresholdLabel={
            !isPercentChart &&
            chartLines.length === 1 &&
            firstLine.prevCloseData?.close
              ? t('chart:previousClose')
              : undefined
          }
        />
      )}
    </div>
  );
};
